import {
  NonEmptyString,
  type OperatorSlug,
  OperatorSlug as OperatorSlugRuntype,
  objectWith,
  PROVIDERS,
  providerOperators,
} from '@ev/shared'
import * as ft from 'funtypes'
import type { Filters } from './filters'

/**
 * Roaming providers as shortcuts over the operator facet.
 *
 * A driver holding one plastic card does not think in operators, they think in
 * "where does this card work". A card here is therefore not a seventh filter
 * dimension: picking one ticks the operator checkboxes it covers, and the map,
 * the URL and the active-filter count all behave as if the driver had ticked
 * them by hand. Several cards can be held at once, and holding two means the
 * union of their networks - which is what a wallet with two cards actually buys
 * you.
 *
 * Two cards are built in, and which brands *those* cover is the eMSP registry's
 * answer rather than this file's: the mapping from each issuer's published list
 * onto registry slugs lives in `@ev/shared`, so the filter panel and the search
 * box cannot disagree about what a card reaches. Names the registry does not
 * track are left off there, which is why a card's operator count can be smaller
 * than the list its issuer advertises.
 *
 * Everything else in the wallet is the driver's own. A card is a wallet, not a
 * dataset: there is no registry of every plastic card in Poland, people hold
 * employer cards and regional ones, and the published issuer lists go stale
 * between deploys. So a card can be defined here, renamed, re-pointed at a
 * different set of brands, or put away - including the two built-in ones, which
 * carry a local override rather than being rewritten, so a registry update
 * still reaches a card nobody has touched.
 *
 * What a card is *not* is URL state. A held card manifests as the operator
 * slugs it ticked, which already travel in `?op=`, so a link shared with
 * somebody who has never heard of the card still shows them the same map. That
 * is also why the frozen codec in `urlState.ts` needs nothing added to it.
 *
 * This sits in the state layer rather than in the filter panel because holding
 * a card is a property of the filters, and the detail rail reads it too: a
 * panel that offers "pay with this card here" and a panel that says the card is
 * held have to agree, and they only can if they ask the same question.
 */

/**
 * A card's identity. The two built-in cards use their provider slug, so an
 * override survives a reinstall and a card the driver defined gets a UUID.
 */
export type ChargeCardId = string

export interface ChargeCard {
  readonly id: ChargeCardId
  /** The name printed on the card. A brand, so the same in both languages. */
  readonly name: string
  /** Registry slugs the card is accepted at, in the issuer's listed order. */
  readonly operators: readonly OperatorSlug[]
  /** The issuer's Telegram bot, without the `@`; absent when it has none. */
  readonly telegramBot: string | undefined
  /**
   * True for the two cards the registry ships.
   *
   * The editor needs to know: putting a built-in away is reversible and a card
   * the driver defined is not, and only the first kind can be restored.
   */
  readonly builtIn: boolean
}

export const BUILT_IN_CHARGE_CARDS: readonly ChargeCard[] = PROVIDERS.map((provider) => ({
  id: provider.slug,
  name: provider.displayName,
  operators: providerOperators(provider),
  telegramBot: provider.telegramBot,
  builtIn: true,
}))

// ---------------------------------------------------------------------------
// The stored wallet
// ---------------------------------------------------------------------------

/**
 * What persistence holds, declared as a runtype because it crosses a trust
 * boundary: `localStorage` is editable by hand, survives a deploy that changed
 * this shape, and is shared with every other script on the origin.
 *
 * An operator slug the registry has since retired is dropped on the way in by
 * {@link OperatorSlug} rather than carried, which is the same answer the URL
 * codec gives an unknown `?op=` value.
 */
export const CustomChargeCard = objectWith(
  {
    id: NonEmptyString,
    name: NonEmptyString,
    operators: ft.Array(OperatorSlugRuntype),
  },
  {},
)
export type CustomChargeCard = ft.Static<typeof CustomChargeCard>

/**
 * A local edit to one of the built-in cards.
 *
 * Stored as a patch rather than as a copy of the card so that a registry
 * change - a network added to an issuer's list, a brand renamed - still reaches
 * a driver who only renamed the card. `deleted` is how a built-in is put away:
 * the card itself is still in the build, which is what makes restoring it
 * possible.
 */
export const ChargeCardOverride = objectWith(
  {},
  {
    name: NonEmptyString,
    operators: ft.Array(OperatorSlugRuntype),
    deleted: ft.Literal(true),
  },
)
export type ChargeCardOverride = ft.Static<typeof ChargeCardOverride>

export const ChargeCardWallet = objectWith(
  {
    custom: ft.Array(CustomChargeCard),
    /** Keyed by built-in card id. A key no build knows is ignored on merge. */
    overrides: ft.Record(ft.String, ChargeCardOverride),
  },
  {},
)
export type ChargeCardWallet = ft.Static<typeof ChargeCardWallet>

export const EMPTY_WALLET: ChargeCardWallet = { custom: [], overrides: {} }

/**
 * The cards the panel shows: the built-in ones with any local edit applied,
 * then the driver's own in the order they were defined.
 *
 * Derived on every read rather than stored merged, so a registry update lands
 * without a migration and an override that no longer names a built-in card
 * simply stops applying.
 */
export const mergeChargeCards = (
  wallet: ChargeCardWallet,
  builtIns: readonly ChargeCard[] = BUILT_IN_CHARGE_CARDS,
): readonly ChargeCard[] => {
  const edited = builtIns.flatMap((card) => {
    const override = wallet.overrides[card.id]
    if (override?.deleted === true) return []
    return [
      {
        ...card,
        name: override?.name ?? card.name,
        operators: override?.operators ?? card.operators,
      },
    ]
  })

  const own = wallet.custom.map((card) => ({
    id: card.id,
    name: card.name,
    operators: card.operators,
    // A card the driver defined has no issuer behind it, so no bot to open.
    telegramBot: undefined,
    builtIn: false,
  }))

  return [...edited, ...own]
}

/** Whether anything is left to restore - the only thing the panel asks. */
export const hasBuiltInEdits = (wallet: ChargeCardWallet): boolean =>
  Object.keys(wallet.overrides).length > 0

// ---------------------------------------------------------------------------
// Holding a card
// ---------------------------------------------------------------------------

/**
 * A card reads as held when every brand it covers is filtered for.
 *
 * Derived rather than stored, so the cards, the operator checkboxes and a
 * pasted `?op=` link can never disagree about what is selected: unticking one
 * brand by hand releases the card, and ticking the last missing one holds it.
 */
export const isChargeCardActive = (filters: Filters, card: ChargeCard): boolean =>
  card.operators.length > 0 && card.operators.every((slug) => filters.operators.includes(slug))

/**
 * Hold the card if it is not held, release it if it is.
 *
 * Releasing keeps the brands the *other* held cards still need - ChargeIn, EDRI,
 * EV Plus and Arinea are on more than one list - and keeps anything ticked by
 * hand outside every list, so putting one card away never quietly narrows the
 * map to something the driver did not ask for.
 *
 * The whole wallet is passed in rather than read from module scope: the cards a
 * driver holds are not a compile-time constant any more, and a function that
 * reached for the built-in two would release brands a card the driver defined
 * still needs.
 */
export const toggleChargeCard = (
  filters: Filters,
  card: ChargeCard,
  cards: readonly ChargeCard[],
): Filters => {
  if (!isChargeCardActive(filters, card)) {
    const added = card.operators.filter((slug) => !filters.operators.includes(slug))
    return { ...filters, operators: [...filters.operators, ...added] }
  }

  const stillCovered = cards
    .filter((other) => other.id !== card.id && isChargeCardActive(filters, other))
    .flatMap((other) => other.operators)

  return {
    ...filters,
    operators: filters.operators.filter(
      (slug) => !card.operators.includes(slug) || stillCovered.includes(slug),
    ),
  }
}

/**
 * A card the driver is holding, at a charger it pays for.
 *
 * Held is the same derivation the filter panel draws its pressed state from, so
 * the shortcut cannot appear for a card the panel shows as released. Both
 * halves have to hold: a card the driver has not picked is not their card, and
 * a card that does not cover this operator would open a bot that cannot start
 * this charge. Cards with no bot are left out - the shortcut is the bot, which
 * is also why a card the driver defined never appears here.
 */
export interface ChargeCardShortcut {
  readonly id: ChargeCardId
  readonly name: string
  readonly telegramBot: string
}

export const chargeCardShortcuts = (
  filters: Filters,
  operator: OperatorSlug,
  cards: readonly ChargeCard[],
): readonly ChargeCardShortcut[] =>
  cards.flatMap((card) =>
    card.telegramBot === undefined ||
    !card.operators.includes(operator) ||
    !isChargeCardActive(filters, card)
      ? []
      : [{ id: card.id, name: card.name, telegramBot: card.telegramBot }],
  )
